import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import { referralAdminApi } from '../services/api';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import { ErrorState } from '../components/common/States';
import { apiError, formatDate, formatDateTimeMsk, formatMoney, formatNumber } from '../utils/format';
import { AGENT_STATUS, CLIENT_STATUS, COMMISSION_STATUS, DELETE_REASONS, EVENT_LABEL, kopecks, referralLink } from '../utils/referral';
import { tokens } from '../theme';

const StatusChip = ({ map, value }) => {
  const st = map[value] || { label: value, bg: tokens.primaryTint, fg: tokens.inkSoft };
  return <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 600 }} />;
};

// Asks for a reason (or a note) and sends it.
const ReasonDialog = ({ open, title, text, label = 'Причина', required = true, confirmLabel, danger, onSubmit, onClose }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setValue(''); setError(''); } }, [open]);
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await onSubmit(value.trim());
      onClose(true);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {text && <Typography sx={{ mb: 2 }}>{text}</Typography>}
        <TextField autoFocus fullWidth multiline minRows={2} label={label} value={value} onChange={(e) => setValue(e.target.value)} inputProps={{ maxLength: 1000 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={busy}>Отмена</Button>
        <Button variant="contained" color={danger ? 'error' : 'primary'} disabled={busy || (required && !value.trim())} onClick={submit}>
          {busy ? <CircularProgress size={18} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Deleting an agent is an archive: a reason from the list is required (a comment too for «Другое»).
const DeleteAgentDialog = ({ open, agent, onSubmit, onClose }) => {
  const [code, setCode] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setCode(''); setComment(''); setError(''); } }, [open]);
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await onSubmit(code, comment.trim());
      onClose(true);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onClose={() => !busy && onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Удалить агента</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Агент «{agent?.name}» пропадёт из общего списка и перестанет засчитывать переходы. Все данные сохранятся: их можно посмотреть в разделе «Удалённые» и при необходимости восстановить агента.
        </Typography>
        <TextField select fullWidth autoFocus label="Причина" value={code} onChange={(e) => setCode(e.target.value)} sx={{ mb: 2 }}>
          {DELETE_REASONS.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline minRows={2} label={code === 'other' ? 'Опишите причину' : 'Комментарий (необязательно)'} value={comment}
          onChange={(e) => setComment(e.target.value)} inputProps={{ maxLength: 1000 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={busy}>Отмена</Button>
        <Button variant="contained" color="error" onClick={submit} disabled={busy || !code || (code === 'other' && !comment.trim())}>
          {busy ? <CircularProgress size={18} /> : 'Удалить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// One agent: link, totals, clients, commissions and history.
const AgentDialog = ({ agentId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [ask, setAsk] = useState(null); // { title, text, label, required, confirmLabel, danger, run }
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(() => {
    setError('');
    referralAdminApi.agent(agentId).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [agentId]);
  useEffect(() => { load(); }, [load]);

  const after = async (promise) => {
    await promise;
    load();
    onChanged();
  };

  // a one-click action (no question asked): errors are shown in the window
  const act = async (promise) => {
    setActionError('');
    try {
      await after(promise);
    } catch (e) {
      setActionError(apiError(e));
    }
  };

  const a = data?.agent;
  const hasCommissions = (data?.commissions || []).some((c) => c.status !== 'canceled');
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {a ? a.name : 'Агент'}
        {a && <Typography variant="body2" color="text.secondary">{a.email}{a.contact ? ` · ${a.contact}` : ''}{a.phone ? ` · ${a.phone}` : ''} · с {formatDate(a.created_at)}</Typography>}
      </DialogTitle>
      <DialogContent dividers>
        {error && <ErrorState message={error} onRetry={load} compact />}
        {!data && !error && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
        {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
        {a && a.status === 'deleted' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>Агент удалён{a.deleted_at ? ` ${formatDate(a.deleted_at)}` : ''}.</b> Причина: {a.deleted_reason || '—'}. Данные сохранены; ссылка не засчитывается.
            {!a.user_id && ' Аккаунт пользователя уже удалён.'}
          </Alert>
        )}
        {a && (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: tokens.bg, border: `1px solid ${tokens.border}` }}>
                <Typography variant="subtitle2">Ссылка</Typography>
                <Typography sx={{ overflowWrap: 'anywhere' }}>{referralLink(a.code)}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <StatusChip map={AGENT_STATUS} value={a.status} />
                  {a.blocked_reason && <Typography variant="caption" color="text.secondary">{a.blocked_reason}</Typography>}
                </Stack>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: tokens.bg, border: `1px solid ${tokens.border}` }}>
                <Typography variant="subtitle2">Итоги</Typography>
                <Typography variant="body2">Ожидает: {kopecks(data.stats.hold_kopecks)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>К выплате: {kopecks(data.stats.available_kopecks)}</Typography>
                <Typography variant="body2">Выплачено: {kopecks(data.stats.paid_kopecks)}</Typography>
              </Box>
            </Stack>

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Клиенты ({data.clients.length})</Typography>
              {data.clients.length === 0 ? <Typography color="text.secondary" variant="body2">Нет клиентов</Typography> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Регистрация</TableCell><TableCell>Оплатил</TableCell><TableCell>Статус</TableCell><TableCell /></TableRow></TableHead>
                    <TableBody>
                      {data.clients.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell sx={{ overflowWrap: 'anywhere' }}>{c.email}</TableCell>
                          <TableCell>{formatDate(c.created_at)}</TableCell>
                          <TableCell>{c.paid ? 'Да' : '—'}</TableCell>
                          <TableCell>
                            <StatusChip map={CLIENT_STATUS} value={c.status} />
                            {c.status_reason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.status_reason}</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            {c.status === 'active' && (
                              <Button size="small" color="error" onClick={() => setAsk({
                                title: 'Исключить клиента', text: 'Его оплаты перестанут приносить агенту вознаграждение, невыплаченные начисления отменятся.',
                                confirmLabel: 'Исключить', danger: true, run: (r) => after(referralAdminApi.setClientStatus(c.id, 'excluded', r)),
                              })}>Исключить</Button>
                            )}
                            {(c.status === 'excluded' || c.status === 'self') && (
                              <Button size="small" onClick={() => setAsk({
                                title: 'Вернуть клиента в программу', label: 'Комментарий', required: false, confirmLabel: 'Вернуть',
                                run: (r) => after(referralAdminApi.setClientStatus(c.id, 'active', r)),
                              })}>Вернуть</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Начисления</Typography>
              {data.commissions.length === 0 ? <Typography color="text.secondary" variant="body2">Нет начислений</Typography> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Клиент</TableCell><TableCell>Оплата</TableCell><TableCell align="right">Сумма оплаты</TableCell><TableCell align="right">Агенту</TableCell><TableCell>Доступно с</TableCell><TableCell>Статус</TableCell><TableCell /></TableRow></TableHead>
                    <TableBody>
                      {data.commissions.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell sx={{ overflowWrap: 'anywhere' }}>{c.client_email}</TableCell>
                          <TableCell>{formatDate(c.paid_at)}</TableCell>
                          <TableCell align="right">{formatMoney(c.paid_rub)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{kopecks(c.amount_kopecks)}</TableCell>
                          <TableCell>{formatDate(c.available_at)}</TableCell>
                          <TableCell>
                            <StatusChip map={COMMISSION_STATUS} value={c.status} />
                            {c.refunded_after_payout && <Typography variant="caption" sx={{ display: 'block', color: tokens.error }}>Оплата отменена после выплаты агенту</Typography>}
                            {c.cancel_reason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.cancel_reason}</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            {(c.status === 'hold' || c.status === 'available') && (
                              <Button size="small" color="error" onClick={() => setAsk({
                                title: 'Отменить начисление', text: `${kopecks(c.amount_kopecks)} не будут выплачены агенту.`,
                                confirmLabel: 'Отменить начисление', danger: true, run: (r) => after(referralAdminApi.cancelCommission(c.id, r)),
                              })}>Отменить</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {data.payouts.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Выплаты</Typography>
                {data.payouts.map((p) => (
                  <Stack key={p.id} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                    <Typography variant="body2">{formatDateTimeMsk(p.created_at)}{p.note ? ` · ${p.note}` : ''}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{kopecks(p.amount_kopecks)}</Typography>
                  </Stack>
                ))}
              </Box>
            )}

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>История</Typography>
              {(data.events || []).map((e, i) => (
                <Box key={i} sx={{ py: 0.5 }}>
                  <Typography variant="body2"><b>{EVENT_LABEL[e.action] || e.action}</b>{e.details ? ` · ${e.details}` : ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{formatDateTimeMsk(e.created_at)}{e.actor ? ` · ${e.actor}` : ''}</Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Закрыть</Button>
        <Box sx={{ flex: 1 }} />
        {a && a.status !== 'deleted' && (
          <Tooltip title={hasCommissions ? 'Есть начисления: сначала выплатите их или отмените' : ''}>
            <span><Button color="error" disabled={hasCommissions} onClick={() => setDeleting(true)}>Удалить</Button></span>
          </Tooltip>
        )}
        {a && a.status === 'deleted' && (
          <Button variant="contained" onClick={() => act(referralAdminApi.restoreAgent(a.id))}>Восстановить</Button>
        )}
        {a && a.status === 'active' && (
          <Button color="error" onClick={() => setAsk({
            title: 'Заблокировать агента', text: 'Новые переходы, клиенты и начисления не будут засчитываться.', confirmLabel: 'Заблокировать', danger: true,
            run: (r) => after(referralAdminApi.setAgentStatus(a.id, 'blocked', r)),
          })}>Заблокировать</Button>
        )}
        {a && (a.status === 'pending' || a.status === 'rejected') && (
          <Button variant="contained" onClick={() => act(referralAdminApi.setAgentStatus(a.id, 'active', ''))}>Одобрить</Button>
        )}
        {a && a.status === 'pending' && (
          <Button color="error" onClick={() => setAsk({
            title: 'Отклонить заявку', text: 'Причину увидит сам человек в своём кабинете. Он сможет исправить данные и подать заявку снова.', label: 'Причина', confirmLabel: 'Отклонить', danger: true,
            run: (r) => after(referralAdminApi.setAgentStatus(a.id, 'rejected', r)),
          })}>Отклонить</Button>
        )}
        {a && a.status === 'blocked' && (
          <Button onClick={() => act(referralAdminApi.setAgentStatus(a.id, 'active', ''))}>Разблокировать</Button>
        )}
        {a && a.status !== 'deleted' && data.stats.available_kopecks > 0 && (
          <Button variant="contained" onClick={() => setAsk({
            title: `Выплата ${kopecks(data.stats.available_kopecks)}`,
            text: 'Сначала переведите деньги агенту (свяжитесь с ним по контактам), затем отметьте выплату здесь. Все начисления «К выплате» станут «Выплачено».',
            label: 'Комментарий (номер платежа, чек самозанятого)', required: false, confirmLabel: 'Отметить выплату',
            run: (r) => after(referralAdminApi.pay(a.id, r)),
          })}>Отметить выплату</Button>
        )}
      </DialogActions>
      <DeleteAgentDialog open={deleting} agent={a}
        onSubmit={async (code, comment) => { await referralAdminApi.deleteAgent(a.id, code, comment); }}
        onClose={(done) => { setDeleting(false); if (done) { onChanged(); onClose(); } }} />
      <ReasonDialog open={Boolean(ask)} {...(ask || {})} onSubmit={(r) => ask.run(r)} onClose={() => setAsk(null)} />
    </Dialog>
  );
};

// /admin/partners — the partner program for administrators.
const AdminPartnersPage = () => {
  const [program, setProgram] = useState(null);
  const [agents, setAgents] = useState(null);
  const [archive, setArchive] = useState([]); // deleted agents
  const [alerts, setAlerts] = useState([]); // payments of clients of deleted agents
  const [view, setView] = useState('main'); // main | deleted
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    setError('');
    Promise.all([referralAdminApi.program(), referralAdminApi.agents(), referralAdminApi.agents(true), referralAdminApi.alerts()])
      .then(([p, a, d, al]) => { setProgram(p.data); setAgents(a.data); setArchive(d.data); setAlerts(al.data); })
      .catch((e) => setError(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = (agents || []).filter((a) => a.status === 'pending');
  const sortedAgents = [...(agents || [])].sort((x, y) => (y.status === 'pending') - (x.status === 'pending'));

  const dismiss = async (id) => {
    try {
      await referralAdminApi.dismissAlert(id);
      load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const recount = async () => {
    setSyncing(true);
    try {
      await referralAdminApi.sync();
      load();
    } catch (e) {
      setError(apiError(e));
    }
    setSyncing(false);
  };
  const setActive = async (active) => {
    const r = await referralAdminApi.setProgram(active);
    setProgram(r.data);
  };

  const totals = (agents || []).reduce((t, a) => ({
    clients: t.clients + a.stats.clients, paid: t.paid + a.stats.paid_clients,
    available: t.available + a.stats.available_kopecks, hold: t.hold + a.stats.hold_kopecks,
  }), { clients: 0, paid: 0, available: 0, hold: 0 });

  return (
    <>
      <PageHeader overline="Администрирование" title="Партнёры" subtitle="Агенты, их клиенты, начисления и выплаты."
        actions={<Button startIcon={<AutorenewRoundedIcon />} onClick={recount} disabled={syncing}>{syncing ? 'Пересчёт…' : 'Пересчитать сейчас'}</Button>} />
      {error && <ErrorState message={error} onRetry={load} />}
      {program && (
        <Card sx={{ mb: 2.5 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <FormControlLabel
                  control={<Switch checked={program.active} onChange={(e) => (e.target.checked ? setActive(true).catch((err) => setError(apiError(err))) : setConfirmStop(true))} />}
                  label={<Typography sx={{ fontWeight: 700 }}>{program.active ? 'Программа работает' : 'Программа остановлена'}</Typography>} />
                <Typography variant="body2" color="text.secondary">
                  {program.percent}% от первой оплаты клиента · выплата через {program.hold_days} дней после оплаты
                  {!program.active && program.deadline && ` · оплаты засчитываются до ${formatDate(program.deadline)}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">Ссылка для партнёров: {window.location.origin}/partner/join (ведёт на регистрацию)</Typography>
              </Box>
              <Stack direction="row" spacing={3}>
                <Box><Typography variant="caption" color="text.secondary">Клиентов</Typography><Typography variant="h6">{formatNumber(totals.clients)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Оплатили</Typography><Typography variant="h6">{formatNumber(totals.paid)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Ожидает</Typography><Typography variant="h6">{kopecks(totals.hold)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">К выплате</Typography><Typography variant="h6">{kopecks(totals.available)}</Typography></Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
      {alerts.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <b>Оплаты у удалённых агентов: {alerts.length}.</b> Комиссия не начислена. Восстановите агента, чтобы её начислить, или закройте уведомление.
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {alerts.map((al) => (
              <Stack key={al.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                  {al.client_email} оплатил {formatMoney(al.paid_rub)} · {formatDate(al.paid_at)} · агент {al.agent_name} ({al.agent_code})
                </Typography>
                <Button size="small" color="inherit" onClick={() => setOpen(al.agent_id)}>Открыть агента</Button>
                <Button size="small" color="inherit" onClick={() => dismiss(al.id)}>Закрыть</Button>
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}
      {pending.length > 0 && view === 'main' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Заявок на рассмотрении: {pending.length}. Откройте заявку, чтобы одобрить или отклонить её.
        </Alert>
      )}
      <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, v) => v && setView(v)} sx={{ mb: 2 }} aria-label="Список агентов">
        <ToggleButton value="main">Агенты ({(agents || []).length})</ToggleButton>
        <ToggleButton value="deleted">Удалённые ({archive.length})</ToggleButton>
      </ToggleButtonGroup>
      {view === 'deleted' && (
        <Card>
          {archive.length === 0 && <EmptyState title="Удалённых агентов нет" description="Удалённые агенты хранятся здесь вместе со всеми данными, их можно восстановить." />}
          {archive.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Агент</TableCell><TableCell>Код</TableCell><TableCell align="right">Клиенты</TableCell>
                    <TableCell>Удалён</TableCell><TableCell>Причина</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archive.map((a) => (
                    <TableRow key={a.id} hover onClick={() => setOpen(a.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.email}{a.contact ? ` · ${a.contact}` : ''}{a.phone ? ` · ${a.phone}` : ''}</Typography>
                        {a.open_alerts > 0 && <Chip size="small" label="есть оплата" sx={{ ml: 1, bgcolor: tokens.errorSoft, color: tokens.error, fontWeight: 600 }} />}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{a.code}</TableCell>
                      <TableCell align="right">{formatNumber(a.stats.clients)}</TableCell>
                      <TableCell>{a.deleted_at ? formatDate(a.deleted_at) : '—'}</TableCell>
                      <TableCell>{a.deleted_reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}
      <Card sx={{ display: view === 'main' ? 'block' : 'none' }}>
        {!agents && !error && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
        {agents && agents.length === 0 && <EmptyState title="Заявок и агентов пока нет" description="Заявки подаются из раздела «Настройки» после регистрации и подтверждения почты." />}
        {agents && agents.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Агент</TableCell><TableCell>Код</TableCell><TableCell align="right">Переходы</TableCell>
                  <TableCell align="right">Клиенты</TableCell><TableCell align="right">Оплатили</TableCell>
                  <TableCell align="right">Ожидает</TableCell><TableCell align="right">К выплате</TableCell><TableCell align="right">Выплачено</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAgents.map((a) => (
                  <TableRow key={a.id} hover onClick={() => setOpen(a.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.email}{a.contact ? ` · ${a.contact}` : ''}{a.phone ? ` · ${a.phone}` : ''}</Typography>
                      {!a.user_id && <Chip size="small" label="аккаунт удалён" sx={{ ml: 1, bgcolor: tokens.warningSoft, color: tokens.warning, fontWeight: 600 }} />}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{a.code}</TableCell>
                    <TableCell align="right">{formatNumber(a.stats.clicks)}</TableCell>
                    <TableCell align="right">{formatNumber(a.stats.clients)}</TableCell>
                    <TableCell align="right">{formatNumber(a.stats.paid_clients)}</TableCell>
                    <TableCell align="right">{kopecks(a.stats.hold_kopecks)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {kopecks(a.stats.available_kopecks)}
                    </TableCell>
                    <TableCell align="right">{kopecks(a.stats.paid_kopecks)}</TableCell>
                    <TableCell><StatusChip map={AGENT_STATUS} value={a.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      {open && <AgentDialog agentId={open} onClose={() => setOpen(null)} onChanged={load} />}
      <ConfirmDialog open={confirmStop} title="Остановить программу?" danger confirmLabel="Остановить"
        text={program ? `Новые агенты и переходы перестанут засчитываться. Переходы, сделанные до остановки, и оплаты клиентов будут засчитываться ещё ${program.grace_days} дней.` : ''}
        onConfirm={() => setActive(false)} onClose={() => setConfirmStop(false)} />
    </>
  );
};

export default AdminPartnersPage;
